import { useEffect, useRef, useState } from "react";
import { useObservatoryCopy } from "./copy";
import { countRange, monthsBetween } from "./model";
import type { ObservatoryView } from "./schema";

export function trendSeries(view: ObservatoryView, selectedThreat?: string) {
  const available = new Set(view.series.map((r) => r.threat));
  const ranked = [...(view.dimensions.attacks ?? [])]
    .sort((a, b) => b.lower - a.lower)
    .map((r) => r.label)
    .filter((name) => available.has(name));
  const names = selectedThreat
    ? [selectedThreat]
    : [...new Set([...ranked, ...available])].slice(0, 2);
  return view.series.filter((r) => names.includes(r.threat));
}

export function ReportingTrendChart({
  view,
  threat,
  miniature = false,
}: {
  view: ObservatoryView;
  threat?: string;
  miniature?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [renderable, setRenderable] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () =>
      setRenderable(element.getBoundingClientRect().width > 0);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { locale, t } = useObservatoryCopy();
  const source = trendSeries(view, threat);
  const rows = source.length
    ? source
    : (view.dimensions.months ?? []).map((m) => ({
        ...m,
        month: m.label,
        threat: t("all"),
      }));
  const names = [...new Set(rows.map((r) => r.threat))];
  const allMonths = [
    ...new Set([
      ...(view.dimensions.months ?? []).map((m) => m.label),
      ...rows.map((r) => r.month),
    ]),
  ].sort();
  const axis = allMonths.length
    ? monthsBetween(allMonths[0]!, allMonths.at(-1)!)
    : [];
  const signature = JSON.stringify({ rows, axis, locale, miniature });
  useEffect(() => {
    const element = ref.current;
    // Keep measuring hidden disclosures so opening an import preview starts rendering.
    if (failed || !element || !renderable) return;
    let disposed = false;
    let cleanup = () => {};
    void Promise.all([
      import("echarts/core"),
      import("echarts/charts"),
      import("echarts/components"),
      import("echarts/renderers"),
    ])
      .then(([ec, charts, components, renderers]) => {
        if (disposed) return;
        ec.use([
          charts.LineChart,
          components.GridComponent,
          components.TooltipComponent,
          renderers.CanvasRenderer,
        ]);
        const chart = ec.init(element, undefined, { renderer: "canvas" });
        cleanup = () => {
          if (!chart.isDisposed()) chart.dispose();
        };
        const update = () => {
          if (disposed || chart.isDisposed()) return;
          const style = getComputedStyle(element);
          const ink = style
            .getPropertyValue("--kavoosh-color-text-muted")
            .trim();
          const colors = [
            style.getPropertyValue("--obs-teal").trim(),
            "#299f9e",
            "#7dc8cc",
          ];
          chart.setOption(
            {
              animation: false,
              color: colors,
              grid: {
                left: miniature ? 12 : 40,
                right: 16,
                top: 12,
                bottom: 32,
              },
              tooltip: {
                trigger: "axis",
                renderMode: "richText",
                confine: true,
                formatter: (items: unknown) => {
                  const values = (
                    Array.isArray(items) ? items : [items]
                  ) as Array<{ dataIndex: number; seriesName: string }>;
                  const month = axis[values[0]?.dataIndex ?? 0];
                  return [
                    month,
                    ...values.map((v) => {
                      const cell = rows.find(
                        (r) => r.month === month && r.threat === v.seriesName,
                      );
                      return `${v.seriesName} · ${countRange(cell)}`;
                    }),
                  ].join("\n");
                },
              },
              xAxis: {
                type: "category",
                data: axis,
                boundaryGap: false,
                axisLine: { lineStyle: { color: ink, opacity: 0.3 } },
                axisTick: { show: false },
                axisLabel: {
                  color: ink,
                  fontSize: 11,
                  hideOverlap: true,
                  interval: axis.length > 12 ? "auto" : 0,
                  formatter: (month: string) => {
                    const d = new Date(`${month}-01T12:00:00Z`);
                    return (
                      new Intl.DateTimeFormat(locale, {
                        month: "short",
                        calendar: "gregory",
                        timeZone: "UTC",
                      }).format(d) + (miniature ? "" : `\n${month.slice(0, 4)}`)
                    );
                  },
                },
              },
              yAxis: {
                type: "value",
                min: 0,
                show: !miniature,
                splitNumber: 3,
                axisLabel: { color: ink, fontSize: 11 },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                  show: !miniature,
                  lineStyle: { color: ink, opacity: 0.15 },
                },
              },
              series: names.map((name, i) => ({
                type: "line",
                name,
                smooth: 0.25,
                connectNulls: false,
                symbol: "circle",
                symbolSize: miniature ? 5 : 6,
                lineStyle: {
                  color: colors[i % colors.length],
                  width: 2.2,
                  type: i === 1 ? "dotted" : "solid",
                },
                itemStyle: {
                  color: "#fff",
                  borderColor: colors[i % colors.length],
                  borderWidth: 1.7,
                },
                ...(i === 0
                  ? {
                      areaStyle: {
                        color: colors[i % colors.length],
                        opacity: 0.09,
                      },
                    }
                  : {}),
                data: axis.map((month) => {
                  const r = rows.find(
                    (p) => p.month === month && p.threat === name,
                  );
                  return r ? (r.lower + r.upper) / 2 : null;
                }),
              })),
            },
            true,
          );
        };
        update();
        const resize = () => {
          if (!chart.isDisposed()) chart.resize();
        };
        const observer = new ResizeObserver(resize);
        observer.observe(element);
        const theme = new MutationObserver(update);
        theme.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ["data-kavoosh-theme"],
        });
        cleanup = () => {
          observer.disconnect();
          theme.disconnect();
          chart.dispose();
        };
      })
      .catch(() => {
        cleanup();
        if (!disposed) setFailed(true);
      });
    return () => {
      disposed = true;
      cleanup();
    };
    // The serialized published series is the complete rendering input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, failed, renderable]);
  return (
    <div
      className={`observatory-trend-chart${miniature ? " miniature" : ""}`}
      ref={ref}
      role="img"
      aria-label={`${t("timeline")}. ${t("dateHelp")}`}
    >
      {failed && (
        <p className="observatory-chart-unavailable">{t("chartUnavailable")}</p>
      )}
    </div>
  );
}
